import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useUser } from '@clerk/clerk-react';
import type { EmailAddressResource } from '@clerk/shared/types';
import { ArrowRight } from 'lucide-react';
import { Modal } from '../../shared/ui/Modal';
import { Button } from '../../shared/ui/Button';
import { useToast } from '../../shared/ui/Toast';
import { useSyncEmailFromClerk } from './hooks';

const emailSchema = z.object({
  email: z.string().email('Enter a valid email'),
});

const otpSchema = z.object({
  code: z.string().length(6, 'Enter the 6-digit code'),
});

type EmailData = z.infer<typeof emailSchema>;
type OtpData = z.infer<typeof otpSchema>;

const inputClass =
  'w-full rounded-xl border border-line bg-surface-raised px-3 py-2.5 text-sm text-text placeholder-muted outline-none focus:border-accent transition-colors';
const labelClass = 'block text-[11px] font-bold text-muted uppercase tracking-widest mb-2';

type Props = {
  open: boolean;
  onClose: () => void;
};

export function ChangeEmailModal({ open, onClose }: Props) {
  const { user } = useUser();
  const { toast } = useToast();
  const syncEmail = useSyncEmailFromClerk();
  const [step, setStep] = useState<'email' | 'verify'>('email');
  const [serverError, setServerError] = useState('');
  const [pendingEmail, setPendingEmail] = useState<EmailAddressResource | null>(null);

  const emailForm = useForm<EmailData>({ resolver: zodResolver(emailSchema) });
  const otpForm = useForm<OtpData>({ resolver: zodResolver(otpSchema) });

  const reset = () => {
    setStep('email');
    setServerError('');
    setPendingEmail(null);
    emailForm.reset();
    otpForm.reset();
  };

  // Don't leave an unverified address hanging on the account if the user backs out
  // (or restarts with a different address) mid-flow.
  const discardPending = () => {
    if (pendingEmail) pendingEmail.destroy().catch(() => {});
  };

  const handleClose = () => {
    discardPending();
    reset();
    onClose();
  };

  const handleBack = () => {
    discardPending();
    setPendingEmail(null);
    setServerError('');
    setStep('email');
  };

  const onSubmitEmail = async ({ email }: EmailData) => {
    if (!user) return;
    setServerError('');
    try {
      const created = await user.createEmailAddress({ email });
      await created.prepareVerification({ strategy: 'email_code' });
      setPendingEmail(created);
      setStep('verify');
    } catch (err: unknown) {
      const clerkErr = err as { errors?: { message: string }[] };
      setServerError(clerkErr.errors?.[0]?.message ?? 'Could not start email change.');
    }
  };

  const onSubmitVerify = async ({ code }: OtpData) => {
    if (!user || !pendingEmail) return;
    setServerError('');
    try {
      const oldEmail = user.primaryEmailAddress;
      await pendingEmail.attemptVerification({ code });
      await user.update({ primaryEmailAddressId: pendingEmail.id });
      if (oldEmail && oldEmail.id !== pendingEmail.id) {
        await oldEmail.destroy().catch(() => {});
      }
      await user.reload();
      // Re-pull the primary email from Clerk's backend API directly rather than waiting on the
      // webhook — otherwise a slow webhook delivery makes the change look like it silently failed.
      await syncEmail.mutateAsync();
      toast('Email address updated.', 'success');
      reset();
      onClose();
    } catch (err: unknown) {
      const clerkErr = err as { errors?: { message: string }[] };
      setServerError(clerkErr.errors?.[0]?.message ?? 'Invalid or expired code.');
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title="Change email address" maxWidth="max-w-sm">
      {step === 'email' ? (
        <form onSubmit={emailForm.handleSubmit(onSubmitEmail)} className="flex flex-col gap-4">
          <div>
            <label className={labelClass}>New email address</label>
            <input
              type="email"
              className={inputClass}
              placeholder="you@company.com"
              autoComplete="email"
              {...emailForm.register('email')}
            />
            {emailForm.formState.errors.email && (
              <p className="text-xs text-danger mt-1">{emailForm.formState.errors.email.message}</p>
            )}
          </div>

          {serverError && (
            <div className="border-l-2 border-danger bg-danger/5 px-4 py-3">
              <p className="text-danger text-sm">{serverError}</p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={handleClose} className="!text-sm normal-case !tracking-normal rounded-xl">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={emailForm.formState.isSubmitting}
              loadingText="Sending code..."
              className="!text-sm normal-case !tracking-normal rounded-xl !px-4 !py-2.5"
            >
              Send code <ArrowRight size={14} />
            </Button>
          </div>
        </form>
      ) : (
        <form onSubmit={otpForm.handleSubmit(onSubmitVerify)} className="flex flex-col gap-4">
          <p className="text-sm text-muted">
            We sent a 6-digit code to <span className="text-text font-semibold">{pendingEmail?.emailAddress}</span>.
          </p>
          <div>
            <label className={labelClass}>Verification code</label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              className={`${inputClass} text-center text-xl font-black tracking-[0.4em]`}
              placeholder="000000"
              autoComplete="one-time-code"
              {...otpForm.register('code')}
            />
            {otpForm.formState.errors.code && (
              <p className="text-xs text-danger mt-1">{otpForm.formState.errors.code.message}</p>
            )}
          </div>

          {serverError && (
            <div className="border-l-2 border-danger bg-danger/5 px-4 py-3">
              <p className="text-danger text-sm">{serverError}</p>
            </div>
          )}

          <div className="flex justify-between items-center gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={handleBack} className="!text-sm normal-case !tracking-normal rounded-xl">
              ← Back
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={otpForm.formState.isSubmitting}
              loadingText="Verifying..."
              className="!text-sm normal-case !tracking-normal rounded-xl !px-4 !py-2.5"
            >
              Verify & save
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
